/**
 * DeveloperPortal — Developer tab for the Coaching Canvas
 *
 * Three sections:
 *  A. Data Upload — WeTransfer URL input + file drag-drop
 *  B. Integrations — Grid of available data connectors with status
 *  C. API Access — Display available endpoints for the user's sport
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  Upload, Link, Globe, Database, Video, Eye, Watch,
  Target, Radio, BarChart3, Circle, Loader2, Check,
  Copy, ExternalLink, Wifi, WifiOff, X,
} from 'lucide-react';
import { C } from '../design-system/primitives/constants';
import { DATA_SOURCES, getSourcesForSport } from './canvasConfig';

const API_BASE = process.env.REACT_APP_API_URL || '';

const ICON_MAP = {
  Upload, Link, Globe, Database, Video, Eye, Watch,
  Target, Radio, BarChart3, Circle, ExternalLink,
};

function getIcon(name) {
  return ICON_MAP[name] || Circle;
}

// ─── Section Header ──────────────────────────────────────────────────────────

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-4">
      <h3 className="text-sm font-medium" style={{ color: C.text }}>{title}</h3>
      {subtitle && <p className="text-[11px] mt-0.5" style={{ color: C.t3 }}>{subtitle}</p>}
    </div>
  );
}

// ─── Data Upload Section ─────────────────────────────────────────────────────

function DataUploadSection({ sport }) {
  const [weTransferUrl, setWeTransferUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleWeTransferSubmit = useCallback(async () => {
    if (!weTransferUrl.trim()) return;
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const res = await fetch(`${API_BASE}/api/video-ingest/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: weTransferUrl.trim(), sport }),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setWeTransferUrl('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 3000);
    }
  }, [weTransferUrl, sport]);

  const handleFileUpload = useCallback(async (file) => {
    if (!file) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await fetch(`${API_BASE}/api/upload`, { method: 'POST', body: formData });
    } catch (err) {
      console.error('Developer portal upload error:', err);
    } finally {
      setIsUploading(false);
    }
  }, []);

  return (
    <div className="p-5 rounded-xl" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
      <SectionHeader
        title="Data Upload"
        subtitle="Upload game film, CSV data, or paste a WeTransfer link"
      />

      {/* WeTransfer URL input */}
      <div className="flex gap-2 mb-4">
        <div className="flex-1 relative">
          <Link size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: C.t3 }} />
          <input
            value={weTransferUrl}
            onChange={e => setWeTransferUrl(e.target.value)}
            placeholder="Paste WeTransfer or file share URL..."
            className="w-full pl-9 pr-3 py-2 rounded-lg text-xs outline-none"
            style={{ background: C.s2, border: `1px solid ${C.borderLight}`, color: C.text }}
          />
        </div>
        <button
          onClick={handleWeTransferSubmit}
          disabled={!weTransferUrl.trim() || isSubmitting}
          className="px-4 py-2 rounded-lg text-xs font-medium transition-colors disabled:opacity-30 flex items-center gap-1.5"
          style={{ background: C.g08, border: `1px solid ${C.goldDim}`, color: C.gold }}
        >
          {isSubmitting ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          Submit
        </button>
      </div>

      {submitStatus && (
        <div className="flex items-center gap-1.5 mb-3 text-[10px]"
          style={{ color: submitStatus === 'success' ? '#00CC66' : '#FF4444' }}>
          {submitStatus === 'success' ? <Check size={12} /> : <X size={12} />}
          {submitStatus === 'success' ? 'Submitted for processing' : 'Submission failed — check URL'}
        </div>
      )}

      {/* File drop zone */}
      <div
        onClick={() => fileInputRef.current?.click()}
        className="flex flex-col items-center justify-center py-8 rounded-xl cursor-pointer transition-colors hover:bg-white/[0.02]"
        style={{ border: `1px dashed ${C.border}` }}
      >
        {isUploading ? (
          <Loader2 size={24} className="animate-spin mb-2" style={{ color: C.gold }} />
        ) : (
          <Upload size={24} className="mb-2" style={{ color: C.t3 }} />
        )}
        <p className="text-xs" style={{ color: C.t2 }}>
          {isUploading ? 'Uploading...' : 'Drop files here or click to browse'}
        </p>
        <p className="text-[10px] mt-1" style={{ color: C.t3 }}>
          Video, images, CSV, PDF — up to 100MB
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="video/*,image/*,text/csv,application/pdf"
        onChange={e => { if (e.target.files?.[0]) handleFileUpload(e.target.files[0]); }}
        className="hidden"
      />
    </div>
  );
}

// ─── Integrations Section ────────────────────────────────────────────────────

function IntegrationsSection({ sport }) {
  const sources = getSourcesForSport(sport);

  return (
    <div className="p-5 rounded-xl" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
      <SectionHeader
        title="Integrations"
        subtitle="Data connectors available for your sport"
      />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
        {sources.map(source => {
          const Icon = getIcon(source.icon);
          return (
            <div
              key={source.id}
              className="flex items-center gap-3 p-3 rounded-xl transition-colors"
              style={{
                background: source.available ? C.s2 : 'transparent',
                border: `1px solid ${source.available ? C.borderLight : C.border}`,
                opacity: source.available ? 1 : 0.5,
              }}
            >
              <div className="p-1.5 rounded-lg" style={{ background: source.available ? C.g08 : C.s2 }}>
                <Icon size={14} style={{ color: source.available ? C.gold : C.t3 }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-medium truncate" style={{ color: C.text }}>
                  {source.name}
                </p>
                {source.vendorName && source.vendorName !== source.name && (
                  <p className="text-[9px] truncate" style={{ color: C.t3 }}>
                    {source.vendorName}
                  </p>
                )}
              </div>
              <div className="flex-shrink-0">
                {source.available ? (
                  <Wifi size={12} style={{ color: '#00CC66' }} />
                ) : (
                  <WifiOff size={12} style={{ color: C.t3 }} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── API Access Section ──────────────────────────────────────────────────────

function ApiAccessSection({ sport }) {
  const [copied, setCopied] = useState(null);

  const endpoints = [
    { label: 'Agent Chat (SSE)', path: '/api/agent/chat', method: 'POST' },
    { label: 'Agent Discovery', path: `/api/agent/agents?sport=${sport}`, method: 'GET' },
    { label: 'File Upload', path: '/api/upload', method: 'POST' },
    { label: 'Video Ingest', path: '/api/video-ingest/submit', method: 'POST' },
    { label: 'Scouting Report', path: '/api/scouting/report', method: 'POST' },
    { label: 'Player Search', path: `/api/natstat/search?sport=${sport}`, method: 'GET' },
  ];

  const handleCopy = useCallback((path) => {
    const fullUrl = `${window.location.origin}${path}`;
    navigator.clipboard.writeText(fullUrl);
    setCopied(path);
    setTimeout(() => setCopied(null), 2000);
  }, []);

  return (
    <div className="p-5 rounded-xl" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
      <SectionHeader
        title="API Access"
        subtitle="Endpoints available for external tool integration"
      />

      <div className="flex items-center gap-2 mb-4 p-3 rounded-lg" style={{ background: C.s2 }}>
        <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.t3 }}>
          Sport Context
        </span>
        <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: C.g08, color: C.gold }}>
          {sport}
        </span>
      </div>

      <div className="space-y-1.5">
        {endpoints.map(ep => (
          <div
            key={ep.path}
            className="flex items-center gap-3 p-2.5 rounded-lg group transition-colors hover:bg-white/[0.02]"
            style={{ border: `1px solid ${C.border}` }}
          >
            <span
              className="text-[9px] font-mono px-1.5 py-0.5 rounded shrink-0"
              style={{
                background: ep.method === 'GET' ? '#00CC6615' : '#0088FF15',
                color: ep.method === 'GET' ? '#00CC66' : '#0088FF',
              }}
            >
              {ep.method}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-medium" style={{ color: C.text }}>{ep.label}</p>
              <p className="text-[10px] font-mono truncate" style={{ color: C.t3 }}>{ep.path}</p>
            </div>
            <button
              onClick={() => handleCopy(ep.path)}
              className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ color: copied === ep.path ? '#00CC66' : C.t3 }}
            >
              {copied === ep.path ? <Check size={12} /> : <Copy size={12} />}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Developer Portal ───────────────────────────────────────────────────

export default function DeveloperPortal({ sport = 'basketball' }) {
  return (
    <div className="h-[calc(100vh-120px)] overflow-auto">
      <div className="max-w-4xl mx-auto space-y-5 p-4">
        <div className="mb-2">
          <h2 className="text-xl font-extralight" style={{ color: C.text }}>
            Developer Portal
          </h2>
          <p className="text-xs" style={{ color: C.t3 }}>
            Upload data, manage integrations, and access APIs
          </p>
        </div>

        <DataUploadSection sport={sport} />
        <IntegrationsSection sport={sport} />
        <ApiAccessSection sport={sport} />
      </div>
    </div>
  );
}
